import { TOUR_IDS } from './onboardingConfig.js';
import { onboardingSelector } from './onboardingConfig.js';
import { waitForSelector } from './onboardingDomUtils.js';

/** @type {Record<string, string>} */
export const TOUR_REQUIRED_STEP = {
  [TOUR_IDS.WELCOME]: 'home',
  [TOUR_IDS.CREATE_PROJECT]: 'form',
  [TOUR_IDS.DASHBOARD]: 'dashboard',
  [TOUR_IDS.ANALYSIS]: 'scenario',
  [TOUR_IDS.REPORT]: 'report',
  [TOUR_IDS.SETTINGS]: 'home',
};

/** First onboarding hook expected after navigation (used to wait for render). */
/** @type {Record<string, string | undefined>} */
export const TOUR_ANCHOR_SELECTOR = {
  [TOUR_IDS.WELCOME]: onboardingSelector('home-hero'),
  [TOUR_IDS.CREATE_PROJECT]: onboardingSelector('form-header'),
  [TOUR_IDS.DASHBOARD]: onboardingSelector('dashboard-overview'),
  [TOUR_IDS.ANALYSIS]: onboardingSelector('scenario-title'),
  [TOUR_IDS.REPORT]: onboardingSelector('report-entered-info'),
  [TOUR_IDS.SETTINGS]: onboardingSelector('profile-persona'),
};

/**
 * @param {string} tourId
 * @param {{
 *   currentStep: string,
 *   currentProperty?: { id?: string } | null,
 *   currentScenario?: { id?: string } | null,
 *   properties?: { id?: string }[],
 *   setCurrentStep: (step: string) => void,
 *   setCurrentProperty?: (p: object) => void,
 * }} ctx
 * @returns {boolean}
 */
export function isTourStartable(tourId, ctx) {
  const { currentStep, currentProperty, currentScenario, properties = [] } = ctx;
  const hasProperty = Boolean(currentProperty?.id || properties[0]?.id);

  switch (tourId) {
    case TOUR_IDS.WELCOME:
    case TOUR_IDS.CREATE_PROJECT:
    case TOUR_IDS.SETTINGS:
      return true;
    case TOUR_IDS.DASHBOARD:
      return hasProperty;
    case TOUR_IDS.ANALYSIS:
      return hasProperty && currentStep === 'scenario';
    case TOUR_IDS.REPORT:
      return hasProperty && currentStep === 'report' && Boolean(currentScenario?.id);
    default:
      return false;
  }
}

/**
 * Navigate to the screen required for a tour.
 * @param {string} tourId
 * @param {{
 *   currentStep: string,
 *   currentProperty?: { id?: string } | null,
 *   currentScenario?: { id?: string } | null,
 *   properties?: { id?: string }[],
 *   setCurrentStep: (step: string) => void,
 *   setCurrentProperty?: (p: object) => void,
 *   force?: boolean,
 * }} ctx
 * @returns {Promise<boolean>}
 */
export async function prepareTourNavigation(tourId, ctx) {
  const {
    currentStep,
    currentProperty,
    currentScenario,
    properties = [],
    setCurrentStep,
    setCurrentProperty,
    force = false,
  } = ctx;

  const requiredStep = TOUR_REQUIRED_STEP[tourId];
  if (!requiredStep) return false;

  if (tourId === TOUR_IDS.SETTINGS) {
    setCurrentStep('home');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('rentalyzer:onboarding-open-profile'));
    }
    const found = await waitForSelector(onboardingSelector('profile-persona'), 3000);
    return found;
  }

  if (tourId === TOUR_IDS.DASHBOARD || tourId === TOUR_IDS.ANALYSIS || tourId === TOUR_IDS.REPORT) {
    const prop = currentProperty?.id ? currentProperty : properties[0];
    if (!prop?.id) return false;
    if (!currentProperty?.id && setCurrentProperty) {
      setCurrentProperty(prop);
    }
  }

  if (tourId === TOUR_IDS.CREATE_PROJECT) {
    setCurrentStep('form');
  } else if (currentStep !== requiredStep) {
    if (tourId === TOUR_IDS.ANALYSIS) {
      if (currentStep !== 'scenario') {
        if (!force) return false;
        setCurrentStep('scenario');
      }
    } else if (tourId === TOUR_IDS.REPORT) {
      if (currentStep !== 'report') {
        if (!force || !currentScenario?.id) return false;
        setCurrentStep('report');
      }
    } else {
      setCurrentStep(requiredStep);
    }
  }

  const anchor = TOUR_ANCHOR_SELECTOR[tourId];
  if (anchor) {
    await new Promise((r) => window.setTimeout(r, 200));
    const found = await waitForSelector(anchor, 5000);
    if (!found && tourId !== TOUR_IDS.WELCOME) {
      return false;
    }
  }

  return true;
}

/** @type {Record<string, string>} Maps app step → contextual tour id */
export const STEP_CONTEXTUAL_TOUR = {
  form: TOUR_IDS.CREATE_PROJECT,
  dashboard: TOUR_IDS.DASHBOARD,
  scenario: TOUR_IDS.ANALYSIS,
  report: TOUR_IDS.REPORT,
};
